/* eslint-disable react-hooks/rules-of-hooks */
import {
    BriefcaseIcon,
    FileIcon,
    CheckIcon,
    FileTextIcon,
    Video,
    ClipboardCheckIcon,
    HomeIcon,
    CreditCardIcon,
    Building,
    Building2Icon,
    LayoutGridIcon,
    Wallet2Icon,
    PhoneIcon,
    WorkflowIcon,
    SettingsIcon,
    LifeBuoyIcon,
    BarChart3Icon,
    Globe,
    MailIcon,
    MessageSquareIcon,
    ClipboardListIcon,
    PackageIcon,
    UsersIcon,
    WandSparklesIcon,
} from "lucide-react";
import { NavItem } from "@type/navItems";
import { useEffect, useState } from "react";
import { useUserProfileContext } from "@context/user_profile";
import LayoutWithMinimalSidebarV2 from "@container/layout_v2";
import { SnackbarProvider } from "@hook/use-snackbar";
import { isOuraEnabled, isOuraRole } from "@oura/role";
import { ouraHref } from "@oura/nav";
import { DOCS_WRITE_ON } from "@oura/docs/writeFlag";
import { REQUESTS_ON } from "@oura/requests/flag";
import { DELIVERY_ON } from "@oura/delivery/flag";

interface Props {
    withoutHeader?: boolean
    children: React.ReactNode
}

export default function AuthLayout(props: Props) {
    const [_, userInfo] = useUserProfileContext();
    const [navItems, setNavItems] = useState<NavItem[]>([])

    useEffect(() => {
        if (userInfo) {
            if (userInfo.role === "hr" || userInfo.role === "admin" || userInfo.role === "super_admin") {
                setNavItems([
                    // ── Recruitment ──
                    {
                        name: "dashboard",
                        tooltip: "Browse candidate profiles",
                        icon: HomeIcon,
                        label: "Dashboard",
                        to: "/dashboard",
                        roles: ["hr", "admin", "super_admin"],
                        group: "recruitment"
                    },
                    {
                        name: "jobs",
                        icon: BriefcaseIcon,
                        tooltip: "Manage job listings and applicants",
                        label: "Jobs",
                        to: "/jobs",
                        roles: ["hr", "admin", "super_admin"],
                        group: "recruitment"
                    },
                    {
                        name: "assessments",
                        icon: ClipboardCheckIcon,
                        tooltip: "Manage forms and AI interviews",
                        label: "Screen",
                        to: "/assess",
                        roles: ["hr", "admin", "super_admin"],
                        group: "recruitment"
                    },
                    // ── Billing (HR) ──
                    {
                        name: "billing",
                        icon: Wallet2Icon,
                        tooltip: "Manage usage and invoices",
                        label: "Billing",
                        to: "/billing",
                        roles: ["hr"],
                        group: "billing"
                    },
                    // "Subscription" menu intentionally hidden on all platforms —
                    // its surfaces (Payment Details / Plans + Workspace Activity)
                    // now live under the Billing menu. The /subscription route is
                    // still reachable by direct URL.
                    // ── Administration (admin / super admin) ──
                    {
                        name: "workspaces",
                        icon: LayoutGridIcon,
                        tooltip: "Manage company workspaces",
                        label: "Workspaces",
                        to: "/workspaces",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    {
                        name: "admin-users",
                        icon: UsersIcon,
                        tooltip: "Look up any user account across workspaces",
                        label: "Users",
                        to: "/admin/users",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    {
                        name: "subscription-packages",
                        icon: PackageIcon,
                        tooltip: "Manage subscription packages",
                        label: "Subscription Packages",
                        to: "/admin/subscription-packages",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    {
                        name: "hiring-activity",
                        icon: BarChart3Icon,
                        tooltip: "Cross-company hiring oversight",
                        label: "Hiring Activity",
                        to: "/hiring-activity",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    {
                        name: "candidate-investigator",
                        icon: LifeBuoyIcon,
                        tooltip: "Investigate candidate screening issues",
                        label: "Investigator",
                        to: "/candidate-investigator",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    {
                        name: "maya-chat-simulator",
                        icon: MessageSquareIcon,
                        tooltip: "Test Maya's replies without a real WhatsApp number",
                        label: "Maya Chat Simulator",
                        to: "/maya-chat-simulator",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    // ── Tools ──
                    {
                        name: "demo",
                        icon: Building,
                        tooltip: "Create a demo workspace",
                        label: "Demo",
                        to: "/demo",
                        roles: ["admin", "super_admin"],
                        group: "tools"
                    },
                    {
                        name: "scheduler-test",
                        icon: MailIcon,
                        tooltip: "Simulate scheduled emails",
                        label: "Scheduler Test",
                        to: "/scheduler-test",
                        roles: ["admin", "super_admin"],
                        group: "tools"
                    },
                    // ── Growth (Oura) ──
                    // Hidden entirely when the product is not launched here, the
                    // same test `OuraRoute` gates on. A menu entry that leads to
                    // /unauthorized is worse than no entry: it advertises a
                    // product and then refuses it.
                    ...(isOuraEnabled()
                        ? [{
                            name: "oura-businesses",
                            icon: Building2Icon,
                            tooltip: "AI visibility for local businesses",
                            label: "Businesses",
                            to: "/oura/businesses",
                            roles: ["admin", "super_admin"],
                            group: "growth",
                        },
                        // Client requests: sample data until its routes ship, as its route is.
                        ...(REQUESTS_ON
                            ? [{
                                name: "oura-requests",
                                icon: MessageSquareIcon,
                                tooltip: "Every change a client asks us for",
                                label: "Requests",
                                to: "/oura/requests",
                                roles: ["admin", "super_admin"],
                                group: "growth",
                            }]
                            : []),
                        // The Skill library (Oura Docs phase 2): only where the
                        // Docs write switch is on, as its route is.
                        ...(DOCS_WRITE_ON
                            ? [{
                                name: "oura-skills",
                                icon: WandSparklesIcon,
                                tooltip: "Where each doc type's skill will plug in",
                                label: "Skills",
                                to: "/oura/skills",
                                roles: ["admin", "super_admin"],
                                group: "growth",
                            }]
                            : []),
                        // The Tracker: every client's delivery on one page, wherever Delivery is served, as its route is.
                        ...(DELIVERY_ON
                            ? [{
                                name: "oura-tracker",
                                icon: ClipboardListIcon,
                                tooltip: "Where every client's delivery stands",
                                label: "Tracker",
                                to: "/oura/tracker",
                                roles: ["admin", "super_admin"],
                                group: "growth",
                            }]
                            : [])]
                        : []),
                ]);
            }

            if (userInfo.role === "agent") {
                setNavItems([
                    {
                        name: "agent-dashboard",
                        tooltip: "Overview and analytics",
                        icon: HomeIcon,
                        label: "Dashboard",
                        to: "/dashboard",
                        roles: ["agent"]
                    },
                    {
                        name: "agent-call-setup",
                        tooltip: "Manage campaigns",
                        icon: WorkflowIcon,
                        label: "Campaigns",
                        to: "/agent/call-setup",
                        roles: ["agent"]
                    },
                    {
                        name: "agent-calls",
                        tooltip: "Monitor and manage calls",
                        icon: PhoneIcon,
                        label: "Calls",
                        to: "/agent/calls",
                        roles: ["agent"]
                    },
                    {
                        name: "agent-settings",
                        tooltip: "Configure voice and language settings",
                        icon: SettingsIcon,
                        label: "Settings",
                        to: "/agent/settings",
                        roles: ["agent"]
                    },
                    // "Subscription" menu intentionally hidden (see note above) —
                    // its surfaces now live under Billing. Route still reachable.
                ]);
            }

            if (userInfo.role === "job_seeker") {
                // Demo-practice mode strips the marketplace-style menu items
                // (Explore / My Jobs / Contract / Payment) so the sidebar
                // only surfaces the practice-interview + resume flow.
                const isDemoPracticeMode = import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true";
                const jobSeekerItems: NavItem[] = [
                    {
                        name: "job-matches",
                        icon: BriefcaseIcon,
                        label: "Explore",
                        to: "/dashboard",
                        roles: ["job_seeker"]
                    },
                    {
                        name: "interview",
                        icon: Video,
                        label: "Interview",
                        to: "/interview",
                        roles: ["job_seeker"]
                    },
                    {
                        name: "resume",
                        icon: FileTextIcon,
                        label: "Resume",
                        to: "/resume",
                        roles: ["job_seeker"]
                    },
                    {
                        name: "my-jobs",
                        icon: CheckIcon,
                        label: "My Jobs",
                        to: "/my-jobs",
                        roles: ["job_seeker"]
                    },
                    {
                        name: "contracts",
                        icon: FileIcon,
                        label: "Contract",
                        to: "/contracts",
                        roles: ["job_seeker"]
                    },
                    {
                        name: "payment",
                        icon: CreditCardIcon,
                        label: "Payment",
                        to: "/payment",
                        roles: ["job_seeker"]
                    },
                ];
                const HIDDEN_IN_DEMO = new Set(["job-matches", "my-jobs", "contracts", "payment"]);
                // In demo-practice mode, surface a "Browse" entry above
                // "Interview" so job_seekers have a public landing destination.
                const browseItem: NavItem = {
                    name: "browse",
                    icon: Globe,
                    label: "Browse",
                    to: "/browse",
                    roles: ["job_seeker"],
                };
                if (isDemoPracticeMode) {
                    const filtered = jobSeekerItems.filter((item) => !HIDDEN_IN_DEMO.has(item.name));
                    const interviewIdx = filtered.findIndex((item) => item.name === "interview");
                    const insertAt = interviewIdx >= 0 ? interviewIdx : 0;
                    filtered.splice(insertAt, 0, browseItem);
                    setNavItems(filtered);
                } else {
                    setNavItems(jobSeekerItems);
                }
            }

            // ── Oura (#3707) ──
            //
            // The three branches above cover hr/admin/super_admin, agent and
            // job_seeker. A real Oura account matched none of them, so
            // `navItems` stayed empty and the only way into the product was to
            // type the URL. The single Oura entry in the first branch did not
            // help: it is gated `roles: ["admin", "super_admin"]`, which is
            // exactly the two roles an Oura account is not.
            //
            // Gated on `isOuraEnabled()` the same way that entry is, and for the
            // same reason its comment gives — a menu entry that leads to
            // /unauthorized is worse than no entry. The two gates stay separate
            // as role.ts documents: roles decide WHO may enter, `isOuraEnabled`
            // decides WHETHER the product is live, so a role claim arriving in a
            // JWT earlier than expected cannot expose an unlaunched product.
            //
            // Destinations come from `ouraHref` rather than string literals, so
            // there is one place that knows where a role lands: staff at the
            // pipeline, a merchant at their own progress.
            if (isOuraRole(userInfo.role)) {
                const staff = userInfo.role === "oura-staff";
                setNavItems(isOuraEnabled()
                    ? [{
                        name: staff ? "oura-businesses" : "oura-dashboard",
                        icon: staff ? Building2Icon : LayoutGridIcon,
                        tooltip: staff
                            ? "AI visibility for local businesses"
                            : "How often AI engines name your business",
                        label: staff ? "Businesses" : "Progress",
                        to: ouraHref(staff ? "businesses" : "dashboard", { role: userInfo.role }),
                        roles: [userInfo.role],
                        group: "growth",
                    },
                    // Staff only: client requests, where its route is served.
                    ...(staff && REQUESTS_ON
                        ? [{
                            name: "oura-requests",
                            icon: MessageSquareIcon,
                            tooltip: "Every change a client asks us for",
                            label: "Requests",
                            to: ouraHref("requests", { role: userInfo.role }),
                            roles: [userInfo.role],
                            group: "growth",
                        }]
                        : []),
                    // Staff only: the Skill library, where the Docs write switch is on.
                    ...(staff && DOCS_WRITE_ON
                        ? [{
                            name: "oura-skills",
                            icon: WandSparklesIcon,
                            tooltip: "Where each doc type's skill will plug in",
                            label: "Skills",
                            to: ouraHref("skills", { role: userInfo.role }),
                            roles: [userInfo.role],
                            group: "growth",
                        }]
                        : []),
                    // Staff only: the Tracker, every client's delivery, where Delivery is served.
                    ...(staff && DELIVERY_ON
                        ? [{
                            name: "oura-tracker",
                            icon: ClipboardListIcon,
                            tooltip: "Where every client's delivery stands",
                            label: "Tracker",
                            to: ouraHref("board", { role: userInfo.role }),
                            roles: [userInfo.role],
                            group: "growth",
                        }]
                        : [])]
                    : []);
            }
        }
    }, [userInfo])

    return (
        <>
            <SnackbarProvider>
                <LayoutWithMinimalSidebarV2
                    navItems={navItems}
                    // TODO: re-enable once Training is ready to ship
                    // productToggle={
                    //     (userInfo?.role === "hr" || userInfo?.role === "admin" || userInfo?.role === "super_admin")
                    //         ? {
                    //             activeLabel: "Recruitment",
                    //             otherLabel: "Training",
                    //             otherPath: "/training",
                    //             cookieValue: "training",
                    //         }
                    //         : undefined
                    // }
                >
                    <div className={`${userInfo?.role === "job_seeker" ? "container px-4 sm:px-8" : userInfo?.role === "agent" ? "px-4 sm:px-8" : "px-4 sm:px-16"} mx-auto`}>
                        <div className="mt-0 sm:mt-8">
                            {props.children}
                        </div>
                    </div>
                </LayoutWithMinimalSidebarV2>
            </SnackbarProvider>
        </>
    );
}
